// src/pages/Dashboard.tsx
// Phase 1 Cleanup: Order status removed - Dashboard is now READ-ONLY
// Ordering workflow moved to Products page → Purchases
// All visual styling preserved

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CheckCircle, RefreshCw, User, Package, Truck, ExternalLink, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, DashboardData } from '../services/dashboardAPI';
import { logger } from '../utils/logger';

const dashboardLogger = logger.createServiceLogger('Dashboard');

interface DashboardProps {
  userRole?: string;
  tenantId?: string;
  organizationName?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ userRole, tenantId, organizationName }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    const timer = logger.startTimer('Dashboard', 'fetchDashboardData');
    
    try {
      setIsLoading(true);
      setError(null);
      
      dashboardLogger.info('Fetching dashboard data', {
        userRole,
        tenantId: tenantId || 'default',
        timestamp: new Date().toISOString()
      });
      
      const data = await dashboardAPI.getDashboardData();
      setDashboardData(data);
      setLastRefresh(new Date());
      
      dashboardLogger.info('Dashboard data loaded successfully', {
        processingTime: data.processingTime,
        criticalAlerts: data.criticalAlerts,
        warningAlerts: data.warningAlerts,
        itemCount: data.lowStockAlerts.totalBulkItems,
        success: data.success
      });
      
      timer();
      
    } catch (err: any) {
      timer();
      dashboardLogger.error('Failed to fetch dashboard data', {
        error: err.message,
        userRole,
        tenantId,
        errorStack: err.stack?.substring(0, 500)
      }, err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dashboardLogger.debug('Dashboard component mounted', { userRole, tenantId });
    fetchDashboardData();
  }, []);

  const handleRefresh = () => {
    dashboardLogger.info('Manual refresh triggered', {
      userRole,
      lastRefresh: lastRefresh.toISOString()
    });
    fetchDashboardData();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Dashboard</h2>
          <p className="text-gray-600">Retrieving live data...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-200 p-8">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <h3 className="text-xl font-bold text-red-900">Dashboard Error</h3>
          </div>
          <p className="text-red-700 mb-6">{error}</p>
          <button onClick={handleRefresh} className="w-full px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">No Dashboard Data Available</h2>
          <button onClick={handleRefresh} className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const formatLastRefresh = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    return date.toLocaleTimeString();
  };

  const displayTenantName = organizationName || tenantId || dashboardData.tenantId;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-6 right-6 z-50 bg-white border-l-4 border-green-500 rounded-lg shadow-xl px-6 py-4 max-w-md">
          <div className="flex items-center">
            <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
            <span className="text-gray-900 font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && dashboardData && (
        <div className="fixed top-6 right-6 z-50 bg-white border-l-4 border-red-500 rounded-lg shadow-xl px-6 py-4 max-w-md">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <span className="text-gray-900 font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-8">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Quartermaster Dashboard</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                  Live from {displayTenantName}
                </span>
                <span>•</span>
                <span>Processing: {dashboardData.processingTime}ms</span>
                <span>•</span>
                <span>Updated {formatLastRefresh(lastRefresh)}</span>
                {userRole && (<><span>•</span><span className="font-medium">{userRole}</span></>)}
              </div>
            </div>
            <button onClick={handleRefresh} disabled={isLoading} className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg">
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Critical Alerts */}
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-red-50 rounded-lg"><AlertTriangle className="h-7 w-7 text-red-600" /></div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-600 mb-1">Critical Alerts</p>
                <p className="text-4xl font-bold text-gray-900">{dashboardData.criticalAlerts}</p>
              </div>
            </div>
            <div className="space-y-1 text-sm text-gray-600 mb-3">
              <div className="flex justify-between"><span>Expired armor:</span><span className="font-semibold text-gray-900">{dashboardData.alertCounts.expiredArmor}</span></div>
              <div className="flex justify-between"><span>Zero stock:</span><span className="font-semibold text-gray-900">{dashboardData.alertCounts.criticalStock}</span></div>
            </div>
            {dashboardData.criticalAlerts > 0 && (
              <div className="pt-3 border-t border-red-100">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">⚠️ Immediate Action Required</span>
              </div>
            )}
          </div>

          {/* Warning Alerts */}
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-yellow-50 rounded-lg"><Clock className="h-7 w-7 text-yellow-600" /></div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-600 mb-1">Warning Alerts</p>
                <p className="text-4xl font-bold text-gray-900">{dashboardData.warningAlerts}</p>
              </div>
            </div>
            <div className="space-y-1 text-sm text-gray-600 mb-3">
              <div className="flex justify-between"><span>Expiring soon:</span><span className="font-semibold text-gray-900">{dashboardData.alertCounts.expiringSoon}</span></div>
              <div className="flex justify-between"><span>Low stock:</span><span className="font-semibold text-gray-900">{dashboardData.alertCounts.lowStock}</span></div>
            </div>
            {dashboardData.warningAlerts > 0 && (
              <div className="pt-3 border-t border-yellow-100">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">📋 Action Recommended</span>
              </div>
            )}
          </div>

          {/* System Status */}
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg"><CheckCircle className="h-7 w-7 text-green-600" /></div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-600 mb-1">System Status</p>
                <p className="text-2xl font-bold text-green-900">Operational</p>
              </div>
            </div>
            <div className="space-y-1 text-sm text-gray-600 mb-3">
              <div className="flex justify-between"><span>GSI queries:</span><span className="font-semibold text-green-700">{dashboardData.processingTime < 2000 ? 'Fast' : 'Slow'}</span></div>
              <div className="flex justify-between"><span>Live data:</span><span className="font-semibold text-green-700">Active</span></div>
            </div>
            <div className="pt-3 border-t border-green-100">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">✓ All Systems Go</span>
            </div>
          </div>
        </div>

        {/* Alert Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Body Armor Alerts */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-white">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <AlertTriangle className="h-6 w-6 mr-3 text-red-600" />Body Armor Status
              </h3>
              <p className="mt-1 text-sm text-gray-600">{dashboardData.bodyArmorAlerts.totalBodyArmor} total items tracked</p>
            </div>
            <div className="p-6">
              {dashboardData.bodyArmorAlerts.expiringSoon.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.bodyArmorAlerts.expiringSoon.slice(0, 3).map((armor) => (
                    <div key={armor.itemId} className="p-5 bg-gradient-to-r from-yellow-50 to-white rounded-xl border border-yellow-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-lg font-bold text-gray-900">{armor.itemName}</p>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">{armor.status}</span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-700">
                        <p><span className="font-medium">Serial:</span> {armor.serialNumber} • <span className="font-medium">Level:</span> {armor.ballisticLevel}</p>
                        {armor.assignedToName ? (
                          <p className="text-blue-700 font-medium">Assigned to: {armor.assignedToName}</p>
                        ) : armor.status === 'Available' ? (
                          <p className="text-gray-500">Unassigned (in stock)</p>
                        ) : null}
                        <p className="text-yellow-700 font-medium">Expires {armor.expirationDate} ({armor.daysUntilExpiration} days remaining)</p>
                      </div>
                    </div>
                  ))}
                  {dashboardData.bodyArmorAlerts.expiringSoon.length > 3 && (
                    <div className="text-center pt-4">
                      <button className="text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline">View all {dashboardData.bodyArmorAlerts.expiringSoon.length} expiring items →</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-gray-900 mb-2">All Body Armor Current</p>
                  <p className="text-sm text-gray-600">No expiration concerns at this time</p>
                </div>
              )}
            </div>
          </div>

        {/* Product Low Stock - Needs Ordering */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Package className="h-6 w-6 mr-3 text-orange-600" />Low Stock - Needs Order
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">{dashboardData.productLowStockAlerts.totalCount} products below minimum (not on order)</p>
                </div>
                {dashboardData.productLowStockAlerts.items.length > 0 && (
                  <button
                    onClick={() => {
                      const printContent = dashboardData.productLowStockAlerts.items.map(item => 
                        `${item.productName}: ${item.currentStock}/${item.minStockLevel} (Order ${item.suggestedOrderQty})`
                      ).join('\n');
                      const printWindow = window.open('', '_blank');
                      if (printWindow) {
                        printWindow.document.write(`<html><head><title>Reorder List</title></head><body><h1>Reorder List - ${new Date().toLocaleDateString()}</h1><pre>${printContent}</pre></body></html>`);
                        printWindow.document.close();
                        printWindow.print();
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Printer className="h-4 w-4 mr-1.5" />
                    Print List
                  </button>
                )}
              </div>
            </div>
            <div className="p-6">
              {dashboardData.productLowStockAlerts.items.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.productLowStockAlerts.items.slice(0, 5).map((item) => (
                    <div key={item.productId} className="flex items-center justify-between p-5 bg-gradient-to-r from-orange-50 to-white rounded-xl border border-orange-200 hover:shadow-md transition-shadow">
                      <div className="flex-1">
                        <p className="text-lg font-bold text-gray-900 mb-1">{item.productName}</p>
                        <p className="text-sm text-orange-700 font-medium">
                          {item.currentStock} / {item.minStockLevel} in stock • Suggest order: {item.suggestedOrderQty}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate('/products', { state: { filterLowStock: true } })}
                        className="ml-4 inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-orange-100 text-orange-800 hover:bg-orange-200"
                      >
                        View <ExternalLink className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                  ))}
                  {dashboardData.productLowStockAlerts.items.length > 5 && (
                    <div className="text-center pt-4">
                      <button 
                        onClick={() => navigate('/products', { state: { filterLowStock: true } })}
                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                      >
                        View all {dashboardData.productLowStockAlerts.items.length} low stock products →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-gray-900 mb-2">Stock Levels Good</p>
                  <p className="text-sm text-gray-600">All products above minimum or already on order</p>
                </div>
              )}
            </div>
          </div>

          {/* On Order - Waiting for Delivery */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Truck className="h-6 w-6 mr-3 text-blue-600" />On Order - Awaiting Delivery
              </h3>
              <p className="mt-1 text-sm text-gray-600">{dashboardData.onOrderAlerts.totalCount} products with {dashboardData.onOrderAlerts.totalPendingQuantity} total items pending</p>
            </div>
            <div className="p-6">
              {dashboardData.onOrderAlerts.items.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.onOrderAlerts.items.slice(0, 5).map((item) => (
                    <div key={item.productId} className="p-5 bg-gradient-to-r from-blue-50 to-white rounded-xl border border-blue-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-lg font-bold text-gray-900">{item.productName}</p>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {item.totalPendingQuantity} pending
                        </span>
                      </div>
                      <div className="space-y-1">
                        {item.orders.slice(0, 2).map((order, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm text-gray-600">
                            <span>
                              {order.vendor || 'Unknown vendor'} • {order.poNumber || 'No PO'} • {order.pendingQuantity} units
                            </span>
                            <button
                              onClick={() => navigate('/purchases')}
                              className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                            >
                              View <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                          </div>
                        ))}
                        {item.orders.length > 2 && (
                          <p className="text-xs text-gray-500">+{item.orders.length - 2} more orders</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {dashboardData.onOrderAlerts.items.length > 5 && (
                    <div className="text-center pt-4">
                      <button 
                        onClick={() => navigate('/purchases')}
                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                      >
                        View all {dashboardData.onOrderAlerts.items.length} on-order products →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Truck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl font-bold text-gray-900 mb-2">No Pending Orders</p>
                  <p className="text-sm text-gray-600">No products currently on order</p>
                </div>
              )}
            </div>
          </div>

          {/* Onboarding Pipeline */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden lg:col-span-2">
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <User className="h-6 w-6 mr-3 text-blue-600" />Onboarding Pipeline
              </h3>
              <p className="mt-1 text-sm text-gray-600">{dashboardData.onboardingAlerts.totalIncomplete} incomplete sessions</p>
            </div>
            <div className="p-6">
              {dashboardData.onboardingAlerts.readyToComplete.length > 0 || dashboardData.onboardingAlerts.overdueSessions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {dashboardData.onboardingAlerts.readyToComplete.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-4">Ready to Complete</h4>
                      <div className="space-y-3">
                        {dashboardData.onboardingAlerts.readyToComplete.map((session) => (
                          <div key={session.sessionId} className="p-4 bg-gradient-to-r from-green-50 to-white rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-gray-900">{session.officerName} <span className="text-gray-600 font-normal">(#{session.badgeNumber})</span></p>
                                <p className="text-sm text-gray-700 mt-1">{session.roleTemplateName} • {session.reservedItems} items reserved</p>
                              </div>
                              <button className="text-sm text-green-700 hover:text-green-900 font-semibold hover:underline">Complete →</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {dashboardData.onboardingAlerts.overdueSessions.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-4">Overdue Sessions</h4>
                      <div className="space-y-3">
                        {dashboardData.onboardingAlerts.overdueSessions.map((session) => (
                          <div key={session.sessionId} className="p-4 bg-gradient-to-r from-red-50 to-white rounded-xl border border-red-200 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-gray-900">{session.officerName} <span className="text-gray-600 font-normal">(#{session.badgeNumber})</span></p>
                                <p className="text-sm text-red-700 font-medium mt-1">{session.roleTemplateName} • {session.daysOld} days old</p>
                              </div>
                              <button className="text-sm text-red-700 hover:text-red-900 font-semibold hover:underline">Resume →</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <User className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-gray-900 mb-2">No Pending Onboarding</p>
                  <p className="text-sm text-gray-600">All officers successfully onboarded</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Footer */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-sm text-gray-600">
            <span className="flex items-center"><CheckCircle className="h-4 w-4 text-green-600 mr-2" />GSI-optimized queries</span>
            <span>•</span>
            <span className="flex items-center"><Package className="h-4 w-4 text-blue-600 mr-2" />Read-only status view</span>
            <span>•</span>
            <span className="text-gray-500">99% cost reduction vs table scans</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;